import React, { useState, useEffect } from 'react';
import { Search, Calendar, User, FileText, RefreshCw, ChevronLeft, ChevronRight, ExternalLink, Trash2 } from 'lucide-react';

export default function MisFacturas() {
  const [facturas, setFacturas] = useState([]);
  const [facturasFiltradas, setFacturasFiltradas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vendedores, setVendedores] = useState([]);
  
  // Filtros
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [vendedorSeleccionado, setVendedorSeleccionado] = useState('');
  const [busquedaCliente, setBusquedaCliente] = useState('');
  
  // Paginación
  const [paginaActual, setPaginaActual] = useState(1);
  const facturasPorPagina = 15;

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      // Cargar facturas locales y vendedores en paralelo
      const [facturasRes, vendedoresRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/local-invoices`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/siigo/users`)
      ]);

      const facturasData = await facturasRes.json();
      const vendedoresData = await vendedoresRes.json();

      if (facturasData.success && facturasData.invoices) {
        setFacturas(facturasData.invoices);
        setFacturasFiltradas(facturasData.invoices);
      }

      if (vendedoresData.users) {
        // Filtrar solo vendedores activos
        const vendedoresActivos = vendedoresData.users.filter(u => u.active === true);
        setVendedores(vendedoresActivos);
      }

    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  // Aplicar filtros
  useEffect(() => {
    let resultado = [...facturas];

    // Filtro por fecha desde
    if (fechaDesde) {
      resultado = resultado.filter(f => {
        const facturaDate = f.date || f.created_at?.split('T')[0];
        return facturaDate >= fechaDesde;
      });
    }

    // Filtro por fecha hasta
    if (fechaHasta) {
      resultado = resultado.filter(f => {
        const facturaDate = f.date || f.created_at?.split('T')[0];
        return facturaDate <= fechaHasta;
      });
    }

    // Filtro por vendedor
    if (vendedorSeleccionado) {
      resultado = resultado.filter(f => f.seller_id === parseInt(vendedorSeleccionado));
    }

    // Filtro por nombre de cliente
    if (busquedaCliente.trim()) {
      const termino = busquedaCliente.toLowerCase().trim();
      resultado = resultado.filter(f => {
        // customer_name puede ser string, array ["Nombre","Apellido"] u objeto
        const raw = f.customer_name;
        let nombreStr = '';
        if (Array.isArray(raw)) nombreStr = raw.filter(Boolean).join(' ');
        else if (typeof raw === 'string') nombreStr = raw;
        else if (raw && typeof raw === 'object') nombreStr = Object.values(raw).filter(v => typeof v === 'string').join(' ');
        const nombreCliente = nombreStr.toLowerCase();
        const idRaw = f.customer_identification;
        const idCliente = (typeof idRaw === 'string' ? idRaw : (idRaw != null ? String(idRaw) : '')).toLowerCase();
        return nombreCliente.includes(termino) || idCliente.includes(termino);
      });
    }

    setFacturasFiltradas(resultado);
    setPaginaActual(1);
  }, [fechaDesde, fechaHasta, vendedorSeleccionado, busquedaCliente, facturas]);

  // Formatear fecha
  const formatearFecha = (fecha) => {
    if (!fecha) return 'N/A';
    const date = new Date(fecha + 'T00:00:00');
    return date.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Formatear moneda
  const formatearMoneda = (valor) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(valor || 0);
  };

  // Eliminar factura local
  const eliminarFactura = async (invoiceId) => {
    if (!window.confirm('¿Está seguro de eliminar esta factura del registro local?')) {
      return;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/local-invoices/${invoiceId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      
      if (data.success) {
        setFacturas(prev => prev.filter(f => f.id !== invoiceId));
      } else {
        alert('Error al eliminar la factura');
      }
    } catch (error) {
      console.error('Error eliminando factura:', error);
      alert('Error al eliminar la factura');
    }
  };

  // Paginación
  const totalPaginas = Math.ceil(facturasFiltradas.length / facturasPorPagina);
  const indexInicio = (paginaActual - 1) * facturasPorPagina;
  const facturasPaginadas = facturasFiltradas.slice(indexInicio, indexInicio + facturasPorPagina);

  // Limpiar filtros
  const limpiarFiltros = () => {
    setFechaDesde('');
    setFechaHasta('');
    setVendedorSeleccionado('');
    setBusquedaCliente('');
  };

  if (loading) {
    return (
      <div className="h-full w-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-lg text-gray-600">Cargando facturas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full bg-gray-50 overflow-auto p-6">
      <div className="max-w-7xl mx-auto">
        {/* Encabezado */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <FileText className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Mis Facturas</h1>
              <p className="text-sm text-gray-500">Registro de facturas creadas desde el módulo de Facturación</p>
            </div>
          </div>
          <button
            onClick={cargarDatos}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <RefreshCw size={18} />
            Actualizar
          </button>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-xl shadow-md p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Filtro fecha desde */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar size={14} className="inline mr-1" />
                Fecha Desde
              </label>
              <input
                type="date"
                value={fechaDesde}
                onChange={(e) => setFechaDesde(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Filtro fecha hasta */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar size={14} className="inline mr-1" />
                Fecha Hasta
              </label>
              <input
                type="date"
                value={fechaHasta}
                onChange={(e) => setFechaHasta(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Filtro vendedor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <User size={14} className="inline mr-1" />
                Vendedor
              </label>
              <select
                value={vendedorSeleccionado}
                onChange={(e) => setVendedorSeleccionado(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Todos los vendedores</option>
                {vendedores.map(v => (
                  <option key={v.id} value={v.id}>
                    {v.first_name} {v.last_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Buscador por cliente */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Search size={14} className="inline mr-1" />
                Buscar Cliente
              </label>
              <input
                type="text"
                value={busquedaCliente}
                onChange={(e) => setBusquedaCliente(e.target.value)}
                placeholder="Nombre o identificación..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Botón limpiar filtros y resumen */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t">
            <button
              onClick={limpiarFiltros}
              className="text-sm text-gray-600 hover:text-blue-600 transition-colors"
            >
              Limpiar filtros
            </button>
            <div className="text-sm text-gray-600">
              Mostrando <span className="font-semibold text-blue-600">{facturasFiltradas.length}</span> facturas
            </div>
          </div>
        </div>

        {/* Tabla de facturas */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Nº Factura
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Vendedor
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Valor de Venta
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {facturasPaginadas.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center gap-3">
                        <FileText className="w-12 h-12 text-gray-300" />
                        <p className="text-lg">No hay facturas registradas</p>
                        <p className="text-sm">Las facturas creadas desde el módulo de Facturación aparecerán aquí</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  facturasPaginadas.map((factura) => (
                    <tr key={factura.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                            <FileText size={18} className="text-blue-600" />
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900">
                              {factura.name || `${factura.prefix || 'FAC'}-${factura.number || 'N/A'}`}
                            </div>
                            <div className="text-xs text-gray-500">
                              {factura.prefix}-{factura.number}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-gray-900">{formatearFecha(factura.date)}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-gray-900 font-medium">
                          {(() => {
                            const raw = factura.customer_name;
                            if (Array.isArray(raw)) return raw.filter(Boolean).join(' ') || 'Cliente';
                            if (typeof raw === 'string' && raw) return raw;
                            if (raw && typeof raw === 'object') {
                              const parts = Object.values(raw).filter(v => typeof v === 'string');
                              return parts.join(' ') || 'Cliente';
                            }
                            return 'Cliente';
                          })()}
                        </div>
                        <div className="text-xs text-gray-500">
                          {factura.customer_identification != null ? String(factura.customer_identification) : 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-gray-700">
                          {factura.seller_name || 'No asignado'}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="text-lg font-bold text-green-600">
                          {formatearMoneda(factura.total)}
                        </div>
                        {factura.balance > 0 && (
                          <div className="text-xs text-orange-500">
                            Saldo: {formatearMoneda(factura.balance)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {factura.public_url && (
                            <a
                              href={factura.public_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
                            >
                              <ExternalLink size={14} />
                              Ver
                            </a>
                          )}
                          <button
                            onClick={() => eliminarFactura(factura.id)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Eliminar del registro"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {totalPaginas > 1 && (
            <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-t">
              <div className="text-sm text-gray-600">
                Página {paginaActual} de {totalPaginas}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPaginaActual(p => Math.max(1, p - 1))}
                  disabled={paginaActual === 1}
                  className={`p-2 rounded-lg transition-colors ${
                    paginaActual === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white border hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <ChevronLeft size={18} />
                </button>
                
                {/* Números de página */}
                {[...Array(Math.min(5, totalPaginas))].map((_, i) => {
                  let pageNum;
                  if (totalPaginas <= 5) {
                    pageNum = i + 1;
                  } else if (paginaActual <= 3) {
                    pageNum = i + 1;
                  } else if (paginaActual >= totalPaginas - 2) {
                    pageNum = totalPaginas - 4 + i;
                  } else {
                    pageNum = paginaActual - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPaginaActual(pageNum)}
                      className={`w-10 h-10 rounded-lg transition-colors ${
                        paginaActual === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'bg-white border hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => setPaginaActual(p => Math.min(totalPaginas, p + 1))}
                  disabled={paginaActual === totalPaginas}
                  className={`p-2 rounded-lg transition-colors ${
                    paginaActual === totalPaginas
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white border hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Resumen total */}
        <div className="mt-6 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-3xl font-bold">{facturasFiltradas.length}</div>
              <div className="text-blue-200 text-sm">Facturas Registradas</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold">
                {formatearMoneda(facturasFiltradas.reduce((sum, f) => sum + (f.total || 0), 0))}
              </div>
              <div className="text-blue-200 text-sm">Total Ventas</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold">
                {formatearMoneda(facturasFiltradas.reduce((sum, f) => sum + (f.balance || 0), 0))}
              </div>
              <div className="text-blue-200 text-sm">Saldo Pendiente</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
